/**
 * v0.5 Stage E.3 — Operator filtering by column type for the
 * reviewer-defined-rule modal.
 *
 * The Add Rule modal exposes the full operator vocabulary, but only
 * those operators that are *meaningful* on the picked column's
 * inferred type are enabled. Filtering here prevents the reviewer
 * from authoring rules the engine would have to reject at evaluation
 * time (e.g. a numeric ``gt`` rule on a string column).
 *
 * Mixed-type columns (v0.5 Stage C.2 — ``inferred_type === "mixed"``)
 * are handled conservatively: the dropdown offers only operators
 * valid for **every** type in the picked ``type_union``. This
 * prevents the reviewer from authoring a rule that's nonsensical
 * for some rows. Sum operators on a mixed-type column require
 * ``"number"`` to be in the union — when present, the rule_engine's
 * per-row subset filter (Stage E.2) handles the numeric-only
 * routing at evaluation time.
 *
 * The mapping below pins the Q2 decision from DECISIONS Stage E.3:
 *   - numeric (integer / number / currency / percentage):
 *       gt / gte / lt / lte / ne / not_null_when / sum_*
 *   - date / datetime:
 *       gt / gte / lt / lte / ne / not_null_when
 *   - string / enum / identifier:
 *       ne / not_null_when
 *   - boolean:
 *       ne / not_null_when
 *
 * `eq` is deliberately not in the engine's vocabulary today (see
 * DECISIONS Stage E.3 — "what would change this"); add it here when
 * the operator lands.
 */
import type { RuleOperator } from './types';

const NUMERIC_TYPES: ReadonlySet<string> = new Set([
  'integer',
  'number',
  'currency',
  'percentage',
]);

const DATE_TYPES: ReadonlySet<string> = new Set(['date', 'datetime']);

const PAIRWISE_NUMERIC_OPS: ReadonlySet<RuleOperator> = new Set<RuleOperator>([
  'gt',
  'gte',
  'lt',
  'lte',
  'ne',
  'not_null_when',
]);

const PAIRWISE_DATE_OPS: ReadonlySet<RuleOperator> = new Set<RuleOperator>([
  'gt',
  'gte',
  'lt',
  'lte',
  'ne',
  'not_null_when',
]);

const STRING_LIKE_OPS: ReadonlySet<RuleOperator> = new Set<RuleOperator>([
  'ne',
  'not_null_when',
]);

const SUM_OPS: ReadonlySet<RuleOperator> = new Set<RuleOperator>([
  'sum_eq',
  'sum_gt',
  'sum_gte',
  'sum_lt',
  'sum_lte',
]);

/**
 * Operators valid for a single inferred type, ignoring mixed-type
 * unions. Used both directly (single-type columns) and as the
 * intersection input (mixed-type columns).
 */
export function operatorsForType(inferredType: string): ReadonlySet<RuleOperator> {
  if (NUMERIC_TYPES.has(inferredType)) {
    return new Set<RuleOperator>([...PAIRWISE_NUMERIC_OPS, ...SUM_OPS]);
  }
  if (DATE_TYPES.has(inferredType)) {
    return new Set<RuleOperator>(PAIRWISE_DATE_OPS);
  }
  // Everything else (string / enum / identifier / boolean / unknown)
  // gets the conservative ne + not_null_when set. The reviewer can
  // still author a not_null_when rule referencing a string column as
  // the condition target.
  return new Set<RuleOperator>(STRING_LIKE_OPS);
}

/**
 * Operators valid for a column based on its RAS column_inference
 * entry. Handles the mixed-type case by intersecting valid
 * operator sets across the picked ``type_union``. Returns an empty
 * set for an empty / malformed mixed union — the modal's caller
 * disables the operator dropdown entirely in that case.
 */
export interface OperatorEligibilityInput {
  inferredType: string;
  /** Populated only when ``inferredType === "mixed"``. */
  typeUnion?: string[] | null;
}

export function operatorsForColumn(
  input: OperatorEligibilityInput,
): ReadonlySet<RuleOperator> {
  if (input.inferredType !== 'mixed') {
    return operatorsForType(input.inferredType);
  }
  const union = input.typeUnion ?? [];
  if (union.length === 0) {
    return new Set<RuleOperator>();
  }
  // Intersection across each picked type's eligible operators.
  let intersection: Set<RuleOperator> | null = null;
  for (const t of union) {
    const eligible = operatorsForType(t);
    if (intersection === null) {
      intersection = new Set(eligible);
      continue;
    }
    for (const op of [...intersection]) {
      if (!eligible.has(op)) intersection.delete(op);
    }
  }
  return intersection ?? new Set<RuleOperator>();
}

/**
 * Convenience predicate. ``true`` iff the operator is valid on
 * every type in the column's union (or on the single type for a
 * non-mixed column).
 */
export function isOperatorValidForColumn(
  operator: RuleOperator,
  column: OperatorEligibilityInput,
): boolean {
  return operatorsForColumn(column).has(operator);
}

/**
 * Filter the part-list dropdown for sum rules. Only numeric-typed
 * columns (or mixed-type columns with ``"number"`` in their union)
 * can participate as parts in a summation rule. The rule_engine's
 * per-row subset filter (Stage E.2) handles non-number rows on
 * mixed-type parts at evaluation time.
 */
export function isColumnEligibleForSumPart(
  column: OperatorEligibilityInput,
): boolean {
  if (column.inferredType === 'mixed') {
    const union = column.typeUnion ?? [];
    return union.includes('number');
  }
  return NUMERIC_TYPES.has(column.inferredType);
}
